import { EMPTY, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { ChatFormFieldType, ChatFormOperation, IChatForm } from '@nimbus-global/features/chat-form/models/chat-form.model';
import {
  IChatFormFieldAction,
  IChatFormFieldValidation,
  IChatFormResolver,
  IChatFormValueUpdate,
} from '@nimbus-global/features/chat-form/models/chat-form-resolver.model';
import { IOptionItem } from '@nimbus-library/features/shared/models/interfaces/i-option-item';
import { CompanionReferenceType } from '@nimbus-library/features/companion-chat/models/companion-conversation.model';

export class ChatMessageFormResolverProxy implements IChatFormResolver {
  private readonly optionsByReferenceType: Record<string, IOptionItem[]> = {};

  public constructor(private readonly inner: IChatFormResolver) {}

  public resolveReferenceOptions = (
    referenceType: string,
    form: IChatForm,
    currentValues?: Record<string, unknown>,
  ): Observable<IOptionItem[]> =>
    this.inner
      .resolveReferenceOptions(referenceType, form, currentValues)
      .pipe(tap((options) => (this.optionsByReferenceType[referenceType] = options)));

  public resolveFieldTitle = (entity: string, field: string): TranslationKeys => this.inner.resolveFieldTitle(entity, field);

  public resolveTitle = (entity: string, operation: ChatFormOperation): TranslationKeys | null =>
    this.inner.resolveTitle?.(entity, operation) ?? null;

  public resolveDescription = (entity: string, operation: ChatFormOperation): TranslationKeys | null =>
    this.inner.resolveDescription?.(entity, operation) ?? null;

  public resolveFieldAction = (entity: string, field: string, currentValues?: Record<string, unknown>): IChatFormFieldAction | null =>
    this.inner.resolveFieldAction?.(entity, field, currentValues) ?? null;

  public resolveFieldMaxLength = (entity: string, field: string): number | null =>
    this.inner.resolveFieldMaxLength?.(entity, field) ?? null;

  public resolveFieldValidation = (entity: string, field: string): IChatFormFieldValidation | null =>
    this.inner.resolveFieldValidation?.(entity, field) ?? null;

  public observeFormValueUpdates = (entity: string): Observable<IChatFormValueUpdate> =>
    this.inner.observeFormValueUpdates?.(entity) ?? EMPTY;

  public getReferenceLabels(form: IChatForm, values: Record<string, unknown>): Record<string, string> | undefined {
    const labels: Record<string, string> = {};
    for (const field of form.fields) {
      if (field.type !== ChatFormFieldType.Reference || !field.referenceType) {
        continue;
      }
      const options = this.optionsByReferenceType[field.referenceType];
      const name = options?.find((o) => o.id === values[field.name])?.name;
      if (name) {
        labels[field.name] = name;
      }
    }
    return Object.keys(labels).length ? labels : undefined;
  }

  public getFormSubmissionValues(form: IChatForm, values: Record<string, unknown>): Record<string, unknown> {
    const hiddenFields = new Set(
      form.fields
        .filter((field) => field.type === ChatFormFieldType.Hidden && field.referenceType !== CompanionReferenceType.Tenant)
        .map((field) => field.name),
    );
    return Object.fromEntries(Object.entries(values).filter(([fieldName]) => !hiddenFields.has(fieldName)));
  }
}
